/**
 * Hook pour gérer les logs
 * @module admin/hooks/useLogs
 */

import { useState, useCallback } from 'react';
import {
  getLogFiles,
  getLogContent,
  cleanupLogs,
} from '../services/maintenanceService';
import { DEFAULT_LOG_CLEANUP_DAYS } from '../constants/defaultValues';

/**
 * Hook de gestion des logs
 * @returns {Object} État et fonctions pour les logs
 */
export const useLogs = () => {
  const [logFiles, setLogFiles] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState(null);
  const [logContent, setLogContent] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [maintenanceError, setMaintenanceError] = useState(null);

  /**
   * Récupère la liste des fichiers de logs
   */
  const fetchLogFiles = useCallback(async () => {
    setLogError(null);
    try {
      const data = await getLogFiles();
      if (data.success) {
        setLogFiles(data.data.files);
      } else {
        setLogError(data.error || 'Erreur lors de la récupération des fichiers de logs');
        setLogFiles([]);
      }
    } catch (err) {
      console.error(
        'Erreur lors de la récupération des fichiers de logs:',
        err
      );
      setLogError(err.message);
      setLogFiles([]);
    }
  }, []);

  /**
   * Récupère le contenu d'un fichier de log
   * @param {string} filename - Nom du fichier
   */
  const fetchLogContent = useCallback(async (filename) => {
    setLogLoading(true);
    setLogError(null);

    try {
      const data = await getLogContent(filename);
      if (data.success) {
        setLogContent(data.data.content);
        setSelectedLogFile(data.data);
      } else {
        setLogError(data.error || 'Erreur lors de la récupération du contenu du log');
        setLogContent('');
        setSelectedLogFile(null);
      }
    } catch (err) {
      setLogError(err.message);
      setLogContent('');
      setSelectedLogFile(null);
    } finally {
      setLogLoading(false);
    }
  }, []);

  /**
   * Nettoie les anciens logs
   * @param {number} daysToKeep - Nombre de jours à conserver
   */
  const handleCleanupLogs = useCallback(async (daysToKeep = DEFAULT_LOG_CLEANUP_DAYS) => {
    setMaintenanceLoading(true);
    setMaintenanceResult(null);
    setMaintenanceError(null);

    try {
      const data = await cleanupLogs(daysToKeep);
      setMaintenanceResult(data);
      await fetchLogFiles();
      
      // Si le fichier sélectionné a été supprimé, le désélectionner
      if (
        selectedLogFile &&
        data.data?.deletedFiles?.includes(selectedLogFile.filename)
      ) {
        setSelectedLogFile(null);
        setLogContent('');
      }
    } catch (err) {
      setMaintenanceError(err.message);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [fetchLogFiles, selectedLogFile]);

  return {
    logFiles,
    selectedLogFile,
    logContent,
    logLoading,
    logError,
    maintenanceLoading,
    maintenanceResult,
    maintenanceError,
    fetchLogFiles,
    fetchLogContent,
    handleCleanupLogs,
  };
};
